import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Chip, Fade } from '@mui/material';
import { EmojiObjects } from '@mui/icons-material';

interface Trivia {
  trivia: string;
  id: string;
  asked: boolean;
}

interface InlineHintRevealProps {
  trivia: Trivia[];
  showTrivia: boolean;
  gameCompleted: boolean;
  onRevealHint: (triviaId: number) => Promise<void>;
  game: any;
}

/**
 * Inline Hint Display Component
 *
 * Displays revealed hints in reverse chronological order (most recently revealed at top).
 * Button is integrated into GuessInput component.
 */
const InlineHintReveal: React.FC<InlineHintRevealProps> = ({
  trivia,
  showTrivia,
  gameCompleted,
  onRevealHint,
  game
}) => {
  const [revealedHints, setRevealedHints] = useState<Trivia[]>([]);

  // Update revealed hints when trivia changes
  useEffect(() => {
    const revealed = trivia.filter(t => t.asked);
    // Reverse order - most recent first
    setRevealedHints([...revealed].reverse());
  }, [trivia]);

  // Don't show anything if hints aren't unlocked yet or if no hints have been revealed
  if (!showTrivia || revealedHints.length === 0) {
    return null;
  }

  return (
    <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ textAlign: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <EmojiObjects sx={{ color: 'secondary.main', fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Revealed Hints
          </Typography>
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}>
        {revealedHints.map((hint, index) => (
          <Fade
            key={hint.id}
            in={true}
            timeout={500}
            style={{ transitionDelay: `${index * 100}ms` }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 2,
                backgroundColor: 'info.lighter',
                border: '1px solid',
                borderColor: 'info.main',
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'info.light',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: 2
                }
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'info.dark',
                  fontWeight: 500,
                  lineHeight: 1.5
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>💡 </Box>
                {hint.trivia}
              </Typography>
              {index === 0 && revealedHints.length > 1 && (
                <Chip
                  label="New"
                  size="small"
                  color="secondary"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    height: 20,
                    fontSize: '0.7rem'
                  }}
                />
              )}
            </Paper>
          </Fade>
        ))}
      </Box>
    </Paper>
  );
};

export default InlineHintReveal;
